// src/features/merchantConfig/lib/typeCoercion.js
//
// User-input → original-type coercion. The OMS Merchant Config API will
// reject a PATCH body with INVALID_PARAMETER_VALUE if a field is sent with
// the wrong type (e.g. sending `"true"` where a `boolean` is expected - see
// MERCHANT_CONFIG_API_DOCS §7.8). Editors talk to the user in strings; this
// module is the single place where we convert back to the canonical type.
//
// We use the ORIGINAL value's runtime type as the source of truth - there is
// no compile-time schema, so we infer "this should be a number" from the
// stored value being a number.

/**
 * Discriminate the canonical type of a value as it would be sent on the wire.
 * Used both for picking an editor and for guarding type coercion.
 *
 * @param {unknown} v
 * @returns {"string" | "long-string" | "number" | "boolean" | "url" | "email" | "iso-datetime" | "null" | "array-of-scalars" | "array-of-objects" | "object" | "unknown"}
 */
export function inferType(v) {
  if (v === null || v === undefined) return "null";
  if (typeof v === "boolean") return "boolean";
  if (typeof v === "number") return "number";
  if (typeof v === "string") {
    if (/^https?:\/\/\S+$/i.test(v)) return "url";
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "email";
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return "iso-datetime";
    if (v.length > 80 || v.includes("\n")) return "long-string";
    return "string";
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "array-of-scalars"; // editor defaults to scalar tag input
    const allScalar = v.every(
      (x) => x === null || ["string", "number", "boolean"].includes(typeof x)
    );
    if (allScalar) return "array-of-scalars";
    const allObject = v.every(
      (x) => x !== null && typeof x === "object" && !Array.isArray(x)
    );
    if (allObject) return "array-of-objects";
    return "array-of-objects"; // fallback - treat mixed as objects
  }
  if (typeof v === "object") return "object";
  return "unknown";
}

/**
 * Coerce a raw string from a text input into the same type as `originalValue`.
 * Throws on hard failures so the caller can surface an inline validation
 * error - never coerces silently to the wrong type.
 *
 * @param {string} input         - raw text from the editor
 * @param {unknown} originalValue - the original value at this path (type source-of-truth)
 * @returns {{ ok: true, value: unknown } | { ok: false, error: string }}
 */
export function coerceFromString(input, originalValue) {
  const t = inferType(originalValue);

  // Strings (including URL / email / iso-datetime / long-string) stay strings.
  if (
    t === "string" ||
    t === "long-string" ||
    t === "url" ||
    t === "email" ||
    t === "iso-datetime" ||
    t === "null"
  ) {
    return { ok: true, value: input };
  }

  if (t === "number") {
    if (input.trim() === "") {
      return { ok: false, error: "Number cannot be empty." };
    }
    const n = Number(input);
    if (!Number.isFinite(n)) {
      return { ok: false, error: "Enter a valid number." };
    }
    return { ok: true, value: n };
  }

  if (t === "boolean") {
    if (input === "true") return { ok: true, value: true };
    if (input === "false") return { ok: true, value: false };
    return { ok: false, error: "Boolean must be true or false." };
  }

  // Arrays / objects shouldn't flow through here - they have their own editors.
  return { ok: false, error: `Unsupported editor type for ${t}.` };
}

/**
 * Convert an ISO-8601 datetime string (e.g. "2026-04-22T16:48:00+05:30") into
 * the value shape an HTML <input type="datetime-local"> expects
 * ("YYYY-MM-DDTHH:mm"). Strips the timezone offset - the UI works in the
 * user's local clock and the API treats the parsed instant as the source of
 * truth (Java side stores the offset as received).
 *
 * @param {string} iso
 * @returns {string}
 */
export function isoToLocalInputValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      "T" +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes())
    );
  } catch {
    return "";
  }
}

/**
 * Convert the value of an <input type="datetime-local"> back into an
 * ISO-8601 string with the user's local timezone offset (so the API sees
 * the exact instant the user intended).
 *
 * @param {string} localInput
 * @returns {string}
 */
export function localInputValueToIso(localInput) {
  if (!localInput) return "";
  try {
    const d = new Date(localInput);
    if (Number.isNaN(d.getTime())) return localInput;
    // `toISOString()` returns UTC ('Z'). For wire stability we keep the
    // offset implicit but use the user's local clock, so we hand-build the
    // string with the local offset.
    const pad = (n) => String(n).padStart(2, "0");
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? "+" : "-";
    const tzAbs = Math.abs(tz);
    const tzH = pad(Math.floor(tzAbs / 60));
    const tzM = pad(tzAbs % 60);
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      "T" +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes()) +
      ":" +
      pad(d.getSeconds()) +
      sign +
      tzH +
      ":" +
      tzM
    );
  } catch {
    return localInput;
  }
}
